import posthog from "posthog-js";

const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;

// ---------------------------------------------------------------------------
// Typed event registry — single source of truth for all event names.
// Call sites get autocomplete and the compiler catches typos.
// ---------------------------------------------------------------------------

export const PH = {
  // Page (PostHog built-ins, fired manually via router.subscribe)
  $PAGEVIEW: "$pageview",
  $PAGELEAVE: "$pageleave",

  // Waiting room
  WAITING_ROOM_ENTERED: "waiting_room_entered",
  DOCTOR_NOTIFIED: "doctor_notified",
  DOCTOR_RENOTIFIED: "doctor_renotified",

  // Consultation status
  CONSULTATION_ACCEPTED: "consultation_accepted",
  CONSULTATION_DECLINED: "consultation_declined",
  CONSULTATION_TIMEOUT: "consultation_timeout",
  CONSULTATION_CANCELLED: "consultation_cancelled",
  CONSULTATION_RETRIED: "consultation_retried",

  // Call lifecycle
  CALL_JOINED: "call_joined",
  CALL_LEFT: "call_left",
  CALL_JOIN_FAILED: "call_join_failed",
  CALL_RECONNECTED: "call_reconnected",
  MODE_SWITCHED: "mode_switched",

  // AV controls
  VIDEO_MUTED: "video_muted",
  VIDEO_UNMUTED: "video_unmuted",
  AUDIO_MUTED: "audio_muted",
  AUDIO_UNMUTED: "audio_unmuted",
  VIDEO_ACTIVATED: "video_activated",
  NETWORK_QUALITY_CHANGED: "network_quality_changed",

  // Media permissions
  MEDIA_PERMISSION_REQUESTED: "media_permission_requested",
  MEDIA_PERMISSION_GRANTED: "media_permission_granted",
  MEDIA_PERMISSION_DENIED: "media_permission_denied",

  // Chat
  MESSAGE_SENT: "message_sent",
  FILE_UPLOADED: "file_uploaded",

  // Doctor workflow
  COMMUNICATION_ENDED: "communication_ended",
  PRESCRIPTION_ADDED: "prescription_added",
  DIAGNOSIS_ADDED: "diagnosis_added",
  TEST_REFERRAL_ADDED: "test_referral_added",
  SPECIALIST_REFERRAL_ADDED: "specialist_referral_added",
  CONSULTATION_CONCLUDED: "consultation_concluded",

  // Patient end
  CONSULTATION_RATED: "consultation_rated",
  DISPUTE_CREATED: "dispute_created",

  // Auth / recovery
  AUTH_RECOVERY_INITIATED: "auth_recovery_initiated",
  AUTH_RECOVERY_SUCCEEDED: "auth_recovery_succeeded",
  AUTH_RECOVERY_FAILED: "auth_recovery_failed",

  // Errors
  ERROR_BOUNDARY_TRIGGERED: "error_boundary_triggered",
  GLOBAL_ERROR: "global_error",
  UNHANDLED_REJECTION: "unhandled_rejection",
  SYNC_FAILURE: "sync_failure",
  APOLLO_AUTH_ERROR: "apollo_auth_error",
  CHUNK_LOAD_ERROR: "chunk_load_error",
} as const;

export type PHEvent = (typeof PH)[keyof typeof PH];

// ---------------------------------------------------------------------------
// Init — call once at app boot, before React mounts.
// ---------------------------------------------------------------------------

export function initPostHog(): void {
  if (!key) {
    if (import.meta.env.DEV) console.warn("[PostHog] VITE_POSTHOG_KEY not set");
    return;
  }

  posthog.init(key, {
    // Data ingest endpoint — must NOT be the dashboard URL
    api_host:
      (import.meta.env.VITE_POSTHOG_HOST as string) ??
      "https://us.i.posthog.com",
    // Toolbar visual editor points at the dashboard, not the ingest host
    ui_host: "https://us.posthog.com",

    // Page tracking is fired manually via router.subscribe in App.tsx
    capture_pageview: false,
    capture_pageleave: true,

    // Autocapture: clicks, form submits, value changes.
    // This powers heatmaps, rage-click, and dead-click with no extra code.
    autocapture: true,

    // Session Replay
    disable_session_recording: false,
    session_recording: {
      // HIPAA baseline: never record keystrokes or input values
      maskAllInputs: true,
      // Add data-ph-mask to any DOM node that renders PHI (name, DOB, etc.)
      maskTextSelector: "[data-ph-mask]",
      // Capture network request headers in the replay timeline
      recordHeaders: true,
      // Do not capture bodies — may contain PHI (auth tokens, patient payloads)
      recordBody: false,
    },

    // Console logs captured inline with replay events
    enable_recording_console_log: true,

    // Web Vitals: LCP, INP, CLS, FCP, TTFB as $performance_event.
    // Replaces the need to manually pipe reportWebVitals into PostHog.
    capture_performance: true,

    // Heatmaps (requires autocapture: true above)
    enable_heatmaps: true,

    // Surveys + any PostHog Site Apps installed on the project
    opt_in_site_apps: true,

    // Only create a person profile row when identify() has been called.
    // Avoids anonymous visitor bloat from consultation visitors.
    person_profiles: "identified_only",

    // Identity and flag state live only for the tab's lifetime.
    // Right for a consultation app where each session has a unique actor.
    persistence: "sessionStorage",
  });

  posthog.register({
    environment: import.meta.env.MODE,
  });
}

// ---------------------------------------------------------------------------
// Identity
// Call as soon as the access token resolves and the actor is known.
// Must be called before phCapture for events to be attributed to the person.
// ---------------------------------------------------------------------------

export function phIdentify(
  userId: string,
  props: {
    name?: string;
    email?: string;
    role: "patient" | "doctor";
    providerId?: string;
    providerName?: string;
  },
): void {
  if (!posthog.__loaded) return;
  posthog.identify(userId, props);
}

// ---------------------------------------------------------------------------
// Group analytics
// 'consultation' groups let you ask "how many consultations had a failed call?"
// 'provider' groups let you segment metrics by clinic/partner.
// ---------------------------------------------------------------------------

export function phGroup(
  type: "consultation" | "provider",
  id: string,
  props?: Record<string, unknown>,
): void {
  if (!posthog.__loaded) return;
  posthog.group(type, id, props);
}

// ---------------------------------------------------------------------------
// Event capture — never throws; analytics must never crash the app.
// ---------------------------------------------------------------------------

export function phCapture(
  event: PHEvent,
  props?: Record<string, unknown>,
): void {
  if (!posthog.__loaded) return;
  try {
    posthog.capture(event, props);
  } catch (error) {
    // swallow
    console.error(`[PostHog] Failed to capture event: ${event}`, props, error);
  }
}

// ---------------------------------------------------------------------------
// Error tracking
// Links exceptions to session replays inside PostHog.
// Always pair with logException(Sentry):
//   Sentry  → alerting, stack-trace detail, on-call paging
//   PostHog → replaying the user journey that led to the error
// ---------------------------------------------------------------------------

export function phCaptureException(
  error: unknown,
  props?: Record<string, unknown>,
): void {
  if (!posthog.__loaded) return;
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    posthog.captureException(err, props);
  } catch {
    // swallow
  }
}

// ---------------------------------------------------------------------------
// Feature flags
// Prefer useFeatureFlagEnabled() in React components.
// Use these helpers in plain TS utilities or outside the component tree.
// ---------------------------------------------------------------------------

export function phIsFeatureEnabled(flag: string): boolean {
  if (!posthog.__loaded) return false;
  return posthog.isFeatureEnabled(flag) ?? false;
}

export function phGetFeatureFlag(flag: string): string | boolean | undefined {
  if (!posthog.__loaded) return undefined;
  return posthog.getFeatureFlag(flag);
}

// ---------------------------------------------------------------------------
// Reset
// Call when a consultation fully ends to prevent cross-contamination between
// patients on shared devices (same pattern as Sentry's clearAuth).
// ---------------------------------------------------------------------------

export function phReset(): void {
  if (!posthog.__loaded) return;
  posthog.reset();
}

// Re-export the raw instance for class components (e.g. ErrorBoundary)
// that cannot use the usePostHog() React hook.
export { posthog };
